<template>
  <div class="relative">
    <Button
      variant="action"
      icon="bell"
      @click.stop="toggleNotifications"
      dusk="notifications-dropdown"
    >
      <template v-if="unreadNotifications">
        <span
          v-if="shouldShowUnreadCount"
          v-html="unreadNotifications > 99 ? '99+' : unreadNotifications"
          class="font-black tracking-normal absolute border-[3px] border-white dark:border-gray-800 top-[-5px] left-[15px] inline-flex items-center justify-center bg-primary-500 rounded-full text-white text-xxs p-[0px] px-1 min-w-[26px]"
        />
        <span
          v-else
          class="absolute border-[3px] border-white dark:border-gray-800 top-0 right-[4px] inline-block bg-primary-500 rounded-full w-4 h-4"
        />
      </template>
    </Button>
  </div>

  <teleport to="body">
    <transition
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="notificationsShown"
        class="panel-modal-backdrop fixed inset-0 z-[115]"
        dusk="notifications-backdrop"
        aria-hidden="true"
        @click="toggleNotifications"
      />
    </transition>

    <transition
      enter-active-class="panel-notification-drawer-enter-active"
      enter-from-class="panel-notification-drawer-enter-from"
      enter-to-class="panel-notification-drawer-enter-to"
      leave-active-class="panel-notification-drawer-leave-active"
      leave-from-class="panel-notification-drawer-leave-from"
      leave-to-class="panel-notification-drawer-leave-to"
    >
      <div
        v-if="notificationsShown"
        ref="notificationPanel"
        tabindex="-1"
        class="panel-glass panel-notification-drawer fixed inset-y-0 end-0 z-[120] flex w-[20rem] flex-col overflow-x-hidden overflow-y-auto shadow-2xl dark:shadow-black/35"
        role="dialog"
        :aria-label="__('Notifications')"
        @keydown.escape.prevent="toggleNotifications"
      >
          <!-- Notification Header -->
          <nav
            v-if="notifications.length > 0"
            class="panel-glass-content panel-glass-divider flex h-14 shrink-0 items-center border-b px-4"
          >
            <Heading :level="3" class="ms-1">{{ __('Notifications') }}</Heading>

            <div class="ms-auto">
              <Dropdown>
                <template #default>
                  <Button
                    :dusk="`notification-center-action-dropdown`"
                    variant="ghost"
                    icon="ellipsis-horizontal"
                  />
                </template>

                <template #menu>
                  <DropdownMenu width="200">
                    <div class="py-1 px-1">
                      <DropdownMenuItem
                        as="button"
                        @click="markAllNotificationsAsRead"
                      >
                        {{ __('Mark all as Read') }}
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        as="button"
                        @click="handleDeleteAllNotifications"
                      >
                        {{ __('Delete all notifications') }}
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenu>
                </template>
              </Dropdown>
            </div>
          </nav>

          <!-- Notification List -->
          <NotificationList
            v-if="notifications.length > 0"
            :notifications="notifications"
          />

          <!-- No Notifications -->
          <div v-else class="panel-glass-content py-12">
            <p class="text-center">
              <svg
                class="inline-block text-zinc-300 dark:text-zinc-500"
                xmlns="http://www.w3.org/2000/svg"
                width="65"
                height="51"
                viewBox="0 0 65 51"
              >
                <path
                  class="fill-current"
                  d="M56 40h2c.552285 0 1 .447715 1 1s-.447715 1-1 1h-2v2c0 .552285-.447715 1-1 1s-1-.447715-1-1v-2h-2c-.552285 0-1-.447715-1-1s.447715-1 1-1h2v-2c0-.552285.447715-1 1-1s1 .447715 1 1v2zm-5.364125-8H38v8h7.049375c.350333-3.528515 2.534789-6.517471 5.5865-8zm-5.5865 10H6c-3.313708 0-6-2.686292-6-6V6c0-3.313708 2.686292-6 6-6h44c3.313708 0 6 2.686292 6 6v25.049375C61.053323 31.5511 65 35.814652 65 41c0 5.522847-4.477153 10-10 10-5.185348 0-9.4489-3.946677-9.950625-9zM20 30h16v-8H20v8zm0 2v8h16v-8H20zm34-2v-8H38v8h16zM2 30h16v-8H2v8zm0 2v4c0 2.209139 1.790861 4 4 4h12v-8H2zm18-12h16v-8H20v8zm34 0v-8H38v8h16zM2 20h16v-8H2v8zm52-10V6c0-2.209139-1.790861-4-4-4H6C3.790861 2 2 3.790861 2 6v4h52zm1 39c4.418278 0 8-3.581722 8-8s-3.581722-8-8-8-8 3.581722-8 8 3.581722 8 8 8z"
                />
              </svg>
            </p>

            <p class="mt-3 text-center text-zinc-600 dark:text-zinc-300">
              {{ __('There are no new notifications.') }}
            </p>

            <p class="mt-6 px-4 text-center">
              <LinkButton
                type="button"
                class="min-h-10 !rounded-xl px-5"
                @click.prevent="toggleNotifications"
              >
                {{ __('Close') }}
              </LinkButton>
            </p>
          </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { Button } from 'laravel-nova-ui'

const { mapMutations, mapActions, mapGetters } = createNamespacedHelpers('nova')

export default {
  components: {
    Button,
  },

  created() {
    this.fetchNotifications()
  },

  watch: {
    notificationsShown(newValue) {
      if (newValue === true) {
        document.body.classList.add('overflow-y-hidden')
        this.$nextTick(() => {
          this.$refs.notificationPanel?.focus()
        })
        return
      }

      document.body.classList.remove('overflow-y-hidden')
    },
  },

  mounted() {
    // Wait a bit for Echo to be fully initialized
    this.$nextTick(() => {
      setTimeout(() => {
        this.setupEchoListener()
      }, 500)
    })
    
    // Track user interaction to unlock audio
    this.setupUserInteractionTracking()
  },

  beforeUnmount() {
    this.removeEchoListener()
    document.body.classList.remove('overflow-y-hidden')
  },

  methods: {
    ...mapMutations(['toggleMainMenu', 'toggleNotifications', 'addNotification', 'updateUnreadCount']),
    ...mapActions([
      'fetchNotifications',
      'deleteNotification',
      'deleteAllNotifications',
      'markNotificationAsRead',
      'markAllNotificationsAsRead',
    ]),

    handleDeleteAllNotifications() {
      if (
        confirm(
          this.__('Are you sure you want to delete all the notifications?')
        )
      ) {
        this.deleteAllNotifications()
      }
    },

    setupEchoListener() {
      // Check if echo method exists and is a function
      if (typeof Nova.echo !== 'function') {
        console.warn('[Nova] Echo is not available. Make sure Pusher is configured.')
        return
      }

      const echo = Nova.echo()
      const userId = Nova.config('userId')

      if (!echo) {
        console.warn('[Nova] Echo instance is null. Retrying in 1 second...')
        setTimeout(() => this.setupEchoListener(), 1000)
        return
      }

      if (!userId) {
        console.warn('[Nova] User ID is not available')
        return
      }

      try {
        const channelName = `nova.notifications.${userId}`
        // console.log('[Nova] Subscribing to channel:', channelName)
        
        this.echoChannel = echo.channel(channelName)
        
        // Add connection event listeners for debugging
        // this.echoChannel.subscribed(() => {
        //   console.log('[Nova] ✅ Successfully subscribed to channel:', channelName)
        // })
        
        this.echoChannel.error((error) => {
          console.error('[Nova] ❌ Channel subscription error:', error)
        })
        
        // Listen for subscription errors
        echo.connector.pusher.connection.bind('error', (err) => {
          console.error('[Nova] Pusher connection error:', err)
        })
        
        // echo.connector.pusher.connection.bind('disconnected', () => {
        //   console.warn('[Nova] Pusher disconnected')
        // })
        
        // echo.connector.pusher.connection.bind('connected', () => {
        //   console.log('[Nova] Pusher connected')
        // })
        
        this.echoChannel.listen('.notification.created', (data) => {
          // console.log('[Nova] 📬 Notification received:', data)
          // The notification data comes directly in the event payload
          if (data && data.notification) {
            // Add created_at_friendly if not present
            if (!data.notification.created_at_friendly && data.notification.created_at) {
              const date = new Date(data.notification.created_at)
              data.notification.created_at_friendly = this.formatRelativeTime(date)
            }
            // Add the new notification to the store
            this.addNotification(data.notification)
            
            // Play notification sound
            this.playNotificationSound()
            
            // Show toast notification
            this.showNotificationToast(data.notification)
          }
        })
      } catch (error) {
        console.error('[Nova] Error setting up Echo listener:', error)
      }
    },

    removeEchoListener() {
      if (this.echoChannel && typeof Nova.echo === 'function') {
        try {
          const echo = Nova.echo()
          const userId = Nova.config('userId')
          if (echo && userId) {
            echo.leave(`nova.notifications.${userId}`)
          }
        } catch (error) {
          console.error('[Nova] Error removing Echo listener:', error)
        }
        this.echoChannel = null
      }
    },

    formatRelativeTime(date) {
      const now = new Date()
      const diff = now - date
      const seconds = Math.floor(diff / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)

      if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'} ago`
      if (hours > 0) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
      if (minutes > 0) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`
      return 'just now'
    },

    setupUserInteractionTracking() {
      // Track any user interaction to unlock audio
      const unlockAudio = () => {
        if (this.audioContextUnlocked) {
          return
        }
        
        this.userHasInteracted = true
        
        // Try to unlock audio by playing a silent sound
        try {
          const audio = new Audio('/notification.mp3')
          audio.volume = 0
          const playPromise = audio.play()
          
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                // Audio unlocked successfully
                audio.pause()
                audio.currentTime = 0
                this.audioContextUnlocked = true
              })
              .catch(() => {
                // Failed to unlock, but that's okay
                // We'll try again on next interaction
              })
          }
        } catch (error) {
          // Ignore errors during unlock attempt
        }
      }
      
      // Listen for various user interactions
      const events = ['click', 'touchstart', 'keydown', 'mousedown']
      events.forEach(event => {
        document.addEventListener(event, unlockAudio, { once: true, passive: true })
      })
    },

    playNotificationSound() {
      try {
        // Check if user has disabled sounds (stored in localStorage)
        const soundEnabled = localStorage.getItem('nova.notificationSound') !== 'false'
        
        if (!soundEnabled) {
          return
        }

        // Only play sound if user has interacted with the page
        if (!this.userHasInteracted) {
          // Silently skip - user hasn't interacted yet
          return
        }

        // Use HTML5 Audio API to play the notification sound file
        const audio = new Audio('/notification.mp3')
        
        // Set volume (0.0 to 1.0)
        audio.volume = 0.7
        
        // Play the sound
        audio.play().catch(() => {
          // Silently fail if sound cannot be played
          // Don't log to console to avoid cluttering
        })
      } catch (error) {
        // Silently fail if sound cannot be played
        // Don't log to console to avoid cluttering
      }
    },

    showNotificationToast(notification) {
      try {
        // Check if notification has a message
        if (!notification || !notification.message) {
          return
        }

        // Get notification type, default to 'info' if not specified
        const type = notification.type || 'info'
        
        // Map notification types to toast types
        const toastType = ['success', 'error', 'warning', 'info'].includes(type) 
          ? type 
          : 'info'

        // Show toast using Nova's toast system
        if (Nova && Nova.$toasted) {
          Nova.$toasted.show(`${this.__("You have a new notification: :title", { title: notification.message })}`, { type: toastType })
        }
      } catch (error) {
        // Silently fail if toast cannot be shown
        console.warn('[Nova] Could not show notification toast:', error)
      }
    },
  },

  data() {
    return {
      echoChannel: null,
      userHasInteracted: false,
      audioContextUnlocked: false,
    }
  },

  computed: {
    ...mapGetters([
      'mainMenuShown',
      'notificationsShown',
      'notifications',
      'unreadNotifications',
    ]),

    shouldShowUnreadCount() {
      return Nova.config('showUnreadCountInNotificationCenter')
    },
  },
}
</script>
